import "./receipts.scss";
import * as React from "react";
import { type IconProp } from "@fortawesome/fontawesome-svg-core";
import { Select } from "@onshopfront/components";
import { determineDataSource } from "../../Data/DataStores/DataSource";
import GeneralSellScreenError from "../../Routes/SellScreen/Errors/GeneralSellScreenError";
import ReceiptHelper, { type ReceiptPaymentData, type ReceiptSaleData } from "../../Utilities/Domain/ReceiptHelper";
import FontAwesomeLight from "../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import { isTrainingMode } from "../../Utilities/Platform/Options/Options";
import { print, PrintContext } from "../../Utilities/UI/PrintHelper";
import { printTextReceipt } from "../../Workers/HardwareConnect/HardwareConnectEvents";
import Button from "../Buttons/Button";
import Receipt from "./Receipt";

export interface ReceiptPrintActionType {
    icon: IconProp;
    label: string;
    onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
    classes?: Array<string>;
    disabled?: boolean;
}

interface Props {
    data: ReceiptSaleData | ReceiptPaymentData;
    dialog?: boolean;
    print?: boolean;
    account?: boolean;
    hideActions?: boolean;
    defaultReceipt?: string;
    actions?: Array<ReceiptPrintActionType>;
    onPrint?: () => void;
    onComplete?: () => void;
}

interface State {
    error: boolean;
    loading: boolean;
    ready: boolean;
    options: Array<{ label: string; value: string; }>;
    receipts: Record<string, any>;
    selected: null | { label: string; value: string; };
    components: Array<any>;
}

function convertType(type: string): string {
    switch(type) {
        case "receipt-full":
            return "normal";
        case "receipt-text":
            return "text_only";
        default:
            return type;
    }
}

export default class ReceiptPrint extends React.Component<Props, State> {
    public static contextType = PrintContext;
    protected printWhenReady: boolean | undefined;

    constructor(props: Props) {
        super(props);

        this.state = {
            error     : false,
            loading   : true,
            ready     : false,
            options   : [],
            selected  : null,
            components: [],
            receipts  : {},
        };

        this.printWhenReady = this.props.print;
    }

    public componentDidCatch(err: Error, errorInfo: React.ErrorInfo): void {
        console.error("Error caught by SaleReceiptPrint", err, errorInfo);
        this.setState({
            error: true,
        });
    }

    public componentDidMount(): void {
        this.loadReceipts();
    }

    public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        if(prevProps.print !== this.props.print) {
            this.printWhenReady = this.props.print;
        }

        if(this.printWhenReady && !prevState.ready && this.state.ready) {
            this.printWhenReady = false;
            this.finalizePrint();
        }
    }

    protected loadReceipts = (): Promise<void> => {
        this.setState({
            loading: true,
        });

        return determineDataSource()
            .then(source => {
                return source.receipts.filter(receipt => {
                    return receipt.type !== "email" && receipt.accountReceipt === !!this.props.account;
                });
            })
            .then(results => {
                const options = [];
                const receipts: Record<string, any> = {};

                let selected = null;
                let components = [];

                // Prepare the receipts
                for(let i = 0, l = results.length; i < l; i++) {
                    const option = {
                        label: results[i].name,
                        value: results[i].uuid,
                    };

                    // Find default receipt
                    if(!selected && this.props.defaultReceipt && results[i].uuid === this.props.defaultReceipt) {
                        selected = option;
                        components = JSON.parse(results[i].components);
                    }

                    options.push(option);
                    receipts[results[i].uuid] = {
                        ...results[i],
                        type      : convertType(results[i].type),
                        components: JSON.parse(results[i].components),
                    };
                }

                // Set selected as the first receipt if default receipt not found
                if(!selected && options.length > 0) {
                    selected = options[0];
                    components = receipts[options[0].value].components;
                }

                this.setState({
                    loading: false,
                    components,
                    receipts,
                    selected,
                    options,
                });
            })
            .catch(err => {
                console.error("Error loading receipts", err);
                this.setState({
                    error  : true,
                    loading: false,
                });
            });
    };

    protected handleReceiptSelect = (value: { label: string; value: string; }): void => {
        this.setState(prevState => ({
            ready     : false,
            selected  : value,
            components: prevState.receipts[value.value].components,
            error     : false,
        }));
    };

    protected handleReceiptReady = (): void => {
        this.setState({
            ready: true,
        });
    };

    protected handlePrintClick = (): void => {
        this.finalizePrint();
    };

    protected finalizePrint = (): void => {
        if(!this.context) {
            return;
        }

        if(!this.state.ready) {
            this.printWhenReady = true;

            return;
        }

        if(!this.state.selected || !this.props.data || !this.state.components) {
            return;
        }

        // Get receipt to print
        const receipt = this.state.receipts[this.state.selected.value];

        if(!receipt) {
            return;
        }

        if(receipt.type === "text_only") {
            printTextReceipt(
                ReceiptHelper.createTextReceipt(
                    receipt.components,
                    this.props.data as Record<string, any>,
                    receipt.receipt_width,
                    isTrainingMode()
                ),
                err => {
                    if(err) {
                        print(false);
                    }
                }
            );
        } else {
            print(receipt.type === "a4");
        }

        if(typeof this.props.onPrint === "function") {
            this.props.onPrint();
        }
    };

    public render(): React.ReactNode {
        if(this.state.error) {
            return (
                <div className="receipt-print">
                    <GeneralSellScreenError onBack={this.props.onComplete}>
                        <p>We&apos;ve encountered an error an were unable to render the receipt</p>
                    </GeneralSellScreenError>
                </div>
            );
        } else if(!this.props.data) {
            return (
                <div className="receipt-print">
                    No data to print
                </div>
            );
        }

        const showTraining = !this.props.dialog && !this.props.account && isTrainingMode();

        let content;

        if(this.state.loading) {
            content = (
                <div className="loading-receipts">
                    <div>Loading Receipts</div>
                    <div><FontAwesomeLight icon="spinner-third" spin /></div>
                </div>
            );
        } else if(!this.state.options.length) {
            content = (
                <div className="receipts not-found">
                    No receipt templates found
                </div>
            );
        } else {
            let receiptRender = null;

            if(this.state.selected) {
                const receipt = this.state.receipts[this.state.selected.value];

                if(receipt) {
                    receiptRender = (
                        <div className="receipt-render-wrapper">
                            <Receipt
                                accountReceipt={this.props.account}
                                type={receipt.type === "text_only" ? "receipt-text" : receipt.type}
                                data={this.props.data as Record<string, any>}
                                padding={receipt.padding}
                                format={this.state.components}
                                onReady={this.handleReceiptReady}
                                width={receipt.receipt_width}
                            />
                            {showTraining && (
                                <div className="receipt-training-mode training-background" />
                            )}
                        </div>
                    );
                }
            }

            content = (
                <React.Fragment>
                    <Select
                        className="receipt-template-select"
                        value={this.state.selected}
                        onChange={this.handleReceiptSelect}
                        options={this.state.options}
                    />
                    <div className={"render-receipt" + (showTraining ? " training-background" : "")}>
                        {receiptRender}
                    </div>
                </React.Fragment>
            );
        }

        const { actions, hideActions } = this.props;

        const receiptActions: Array<ReceiptPrintActionType> = [];

        if(!this.props.dialog) {
            receiptActions.push({
                icon    : "print",
                label   : "Print",
                onClick : this.handlePrintClick,
                disabled: !this.state.ready,
            });
        }

        if(Array.isArray(actions)) {
            receiptActions.push(...actions);
        }

        return (
            <div className="receipt-print">
                <div className="receipt-actions">
                    {!hideActions && receiptActions.map((action, index) => (
                        <Button
                            key={index}
                            type="button"
                            classes={[ "receipt-print-button receipt-action", ...(action.classes || []) ]}
                            onClick={action.onClick}
                        >
                            <span><FontAwesomeLight icon={action.icon} /> {action.label}</span>
                        </Button>
                    ))}
                </div>
                <div className="receipts">
                    {content}
                </div>
                {this.props.dialog && (
                    <div className="done">
                        <Button
                            type="button"
                            classes={[ "receipt-print-button button-save" ]}
                            onClick={this.handlePrintClick}
                        >
                            <span><FontAwesomeLight icon="print" /> Print</span>
                        </Button>
                    </div>
                )}
                {(!this.props.dialog && typeof this.props.onComplete === "function") && (
                    <div className="done">
                        <Button
                            type="button"
                            onClick={this.props.onComplete}
                            classes={[ "receipt-print-button button-save" ]}
                        >
                            Done
                        </Button>
                    </div>
                )}
            </div>
        );
    }
}
